import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { apiPut } from '../../lib/api.js';
import { US_STATES } from '../../lib/usConstants.js';

const EMPTY = { line1: '', line2: '', city: '', state: '', postalCode: '' };

/**
 * Billing address (used for sales-tax calculation).
 * @param {{address?: Partial<typeof EMPTY>}} props
 */
export default function BillingAddressForm({ address }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ ...EMPTY, ...(address || {}) });
  useEffect(() => { setForm({ ...EMPTY, ...(address || {}) }); }, [address]);

  const dirty = JSON.stringify({ ...EMPTY, ...(address || {}) }) !== JSON.stringify(form);
  const valid = form.line1.trim() && form.city.trim() && form.state && /^\d{5}(-\d{4})?$/.test(form.postalCode.trim());
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = useMutation({
    mutationFn: () => apiPut('/billing-center/billing-address', form),
    onSuccess: () => { toast.success('Billing address saved'); qc.invalidateQueries({ queryKey: ['billing-overview'] }); },
    onError: (e) => toast.error(e?.response?.data?.error || 'Could not save address'),
  });

  return (
    <div>
      <h3 className="font-semibold">Billing address</h3>
      <p className="text-xs text-white/60 mb-3">Your billing address is used for calculating the appropriate sales tax. This should be the address where your business is located.</p>
      <div className="space-y-3">
        <input className="input w-full" placeholder="Address" value={form.line1} onChange={set('line1')} maxLength={120} aria-label="Address" />
        <input className="input w-full" placeholder="Address line 2" value={form.line2} onChange={set('line2')} maxLength={120} aria-label="Address line 2" />
        <div className="grid md:grid-cols-2 gap-3">
          <input className="input" placeholder="City" value={form.city} onChange={set('city')} maxLength={80} aria-label="City" />
          <select className="input" value={form.state} onChange={set('state')} aria-label="State">
            <option value="">State</option>
            {US_STATES.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
          </select>
        </div>
        <input className="input w-full md:w-1/2" placeholder="ZIP code" value={form.postalCode} onChange={set('postalCode')} maxLength={10} aria-label="ZIP code" />
      </div>
      <div className="flex justify-end mt-3">
        <button type="button" disabled={!dirty || !valid || save.isPending} onClick={() => save.mutate()} className="btn-primary text-sm disabled:opacity-40">SAVE BILLING ADDRESS</button>
      </div>
    </div>
  );
}
